import { useState, useEffect } from "react";
import {
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon,
  InformationCircleIcon,
  EyeIcon,
  UserIcon,
  CheckCircleIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useStudioUser } from "../hooks/useStudioUser";
import Notification from "../components/Notification";
import RegistrationDetailsPanel from "../components/RegistrationDetailsPanel";
import {
  getExtraRegistrationColumns,
  getRegistrationFieldLabel,
  getRegistrationFieldValue,
} from "../utils/registrationFields";
import { exportRegistrationsToExcel } from "../utils/exportRegistrationsToExcel";

function getLocalDateString() {
  const today = new Date();
  const year = today.getFullYear();
  const month = (today.getMonth() + 1).toString().padStart(2, "0");
  const day = today.getDate().toString().padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Date input formatter function
const formatDateInput = (value: string): string => {
  // Remove all non-digits
  const numbers = value.replace(/\D/g, '');
  
  // Limit to 8 digits (MMDDYYYY)
  const limited = numbers.slice(0, 8);
  
  // Format as MM/DD/YYYY
  if (limited.length >= 5) {
    return `${limited.slice(0, 2)}/${limited.slice(2, 4)}/${limited.slice(4)}`;
  } else if (limited.length >= 3) {
    return `${limited.slice(0, 2)}/${limited.slice(2)}`;
  } else if (limited.length >= 1) {
    return limited;
  }
  
  return '';
};

// Convert database date format to MM/DD/YYYY
const convertDatabaseDateToDisplay = (dateString: string | null): string => {
  if (!dateString) return '';
  
  try {
    // Handle both ISO date strings and YYYY-MM-DD format
    if (dateString.includes('T')) {
      // ISO date string like "2025-07-18T00:00:00+00:00"
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();
      
      return `${month}/${day}/${year}`;
    } else {
      // YYYY-MM-DD format - parse directly without timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      if (!year || !month || !day) return '';
      
      return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
    }
  } catch (error) {
    return '';
  }
};

// Convert MM/DD/YYYY format to ISO date string for database
const convertDisplayDateToDatabase = (dateString: string): string | null => {
  if (!dateString || dateString.trim() === '') return null;
  
  try {
    // Parse MM/DD/YYYY format
    const parts = dateString.split('/');
    if (parts.length !== 3) return null;
    
    const month = parseInt(parts[0]) - 1; // Month is 0-indexed
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    
    // Create date at midnight in local timezone to avoid timezone shifts
    const date = new Date(year, month, day, 0, 0, 0, 0);
    if (isNaN(date.getTime())) return null;
    
    // Format as YYYY-MM-DD to avoid timezone issues
    const yearStr = date.getFullYear().toString();
    const monthStr = (date.getMonth() + 1).toString().padStart(2, '0');
    const dayStr = date.getDate().toString().padStart(2, '0');
    
    return `${yearStr}-${monthStr}-${dayStr}`;
  } catch (error) {
    return null;
  }
};

// Format currency input (e.g., "1234.56" -> "$12.34")
const formatCurrencyInput = (value: string): string => {
  // Remove all non-digit characters except decimal point
  const cleanValue = value.replace(/[^\d.]/g, '');
  
  // Ensure only one decimal point
  const parts = cleanValue.split('.');
  if (parts.length > 2) {
    return parts[0] + '.' + parts.slice(1).join('');
  }
  
  // Limit to 2 decimal places
  if (parts.length === 2 && parts[1].length > 2) {
    return parts[0] + '.' + parts[1].slice(0, 2);
  }
  
  return cleanValue;
};

// Convert currency input to number (e.g., "12.34" -> 12.34)
const parseCurrencyInput = (value: string): number => {
  const cleanValue = value.replace(/[^\d.]/g, '');
  const num = parseFloat(cleanValue);
  return isNaN(num) ? 0 : num;
};

// Security constants
const MAX_LENGTHS: Record<string, number> = {
  searchTerm: 100,
  eventName: 200,
  registrationTitle: 200,
  registrationBody: 2000,
  customTermsConditions: 5000,
  dateInput: 10, // MM/DD/YYYY
  timeInput: 5, // HH:MM
  currencyInput: 10, // $999.99
};

const SUBMISSION_COOLDOWN = 2000; // 2 seconds between submissions

// Input sanitization function
const sanitizeInput = (input: string, fieldType?: string): string => {
  if (typeof input !== 'string') return '';
  
  let sanitized = input;
  
  // For search terms and text fields, preserve spaces but remove dangerous content
  if (fieldType === 'searchTerm' || fieldType === 'eventName' || fieldType === 'registrationTitle' || fieldType === 'registrationBody' || fieldType === 'customTermsConditions') {
    // Only remove dangerous patterns, preserve spaces
    sanitized = sanitized.replace(/[<>]/g, ''); // Remove HTML tags
    sanitized = sanitized.replace(/javascript:/gi, ''); // Remove javascript protocol
    sanitized = sanitized.replace(/vbscript:/gi, ''); // Remove vbscript protocol
    sanitized = sanitized.replace(/on\w+\s*=/gi, ''); // Remove event handlers
    sanitized = sanitized.replace(/data:text\/html/gi, ''); // Remove data URLs
    // Don't trim to preserve user intent
  } else {
    // For other fields (like dates, times, currency), use more aggressive sanitization
    sanitized = sanitized.trim();
    sanitized = sanitized.replace(/[<>]/g, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/data:text\/html/gi, '');
    sanitized = sanitized.replace(/vbscript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
  }
  
  return sanitized;
};

// Enhanced validation function
const validateInput = (value: string, type: string): boolean => {
  if (!value || typeof value !== 'string') return false;
  
  const sanitized = sanitizeInput(value, type);
  if (sanitized.length === 0) return false;

  // Check length limits
  if (MAX_LENGTHS[type] && sanitized.length > MAX_LENGTHS[type]) {
    return false;
  }
  
  // Type-specific validation
  switch (type) {
    case 'searchTerm':
    case 'eventName':
    case 'registrationTitle':
    case 'registrationBody':
    case 'customTermsConditions':
      // Check for dangerous patterns in text fields
      const textDangerousPatterns = /<script\b|javascript:|vbscript:|on\w+\s*=|data:text\/html/i;
      return !textDangerousPatterns.test(sanitized) && sanitized.length >= 1;
    case 'dateInput':
      // Check for MM/DD/YYYY format
      const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
      return dateRegex.test(sanitized);
    case 'timeInput':
      // Check for HH:MM format
      const timeRegex = /^\d{1,2}:\d{2}$/;
      return timeRegex.test(sanitized);
    case 'currencyInput':
      // Check for currency format (digits, decimal point, up to 2 decimal places)
      const currencyRegex = /^\d*\.?\d{0,2}$/;
      return currencyRegex.test(sanitized);
    default:
      return sanitized.length > 0;
  }
};

// Security event logging
const logSecurityEvent = (event: string, details: any) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`Events Security Event: ${event}`, {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      details
    });
  }
};

// Format currency for display (e.g., 12.34 -> "$12.34")
const formatCurrencyDisplay = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

interface ClassInstructor {
  id: string;
  name: string;
  belt: string | null;
  role: string | null;
}

interface Event {
  id: string;
  studio_id: string;
  event_name: string;
  assigned_instructors: ClassInstructor[];
  start_time: string;
  end_time: string;
  type: string;
  from_date: string;
  end_date: string;
  charge_for_event?: boolean;
  charge_amount?: number;
  create_registration_link?: boolean;
  registration_link?: string;
  registration_fields?: string[];
  auto_promote_after_testing?: boolean;
  registration_title?: string;
  registration_body?: string;
  registration_image?: string;
  custom_terms_conditions?: string;
  show_studio_address_on_registration?: boolean;
}

export default function Events() {
  const { user, session, getAuthToken } = useAuth();
  const { studioUser } = useStudioUser();
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [viewingRegistrations, setViewingRegistrations] = useState<Event | null>(null);
  const [registrations, setRegistrations] = useState<any[]>([]);
  const [loadingRegistrations, setLoadingRegistrations] = useState(false);
  const [availableInstructors, setAvailableInstructors] = useState<
    ClassInstructor[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [studioTimezone, setStudioTimezone] =
    useState<string>("America/New_York");
  const [localTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  const [registrationSearchTerm, setRegistrationSearchTerm] = useState<string>("");
  const [filteredRegistrations, setFilteredRegistrations] = useState<any[]>([]);
  const [isStripeConnected, setIsStripeConnected] = useState(false);
  const [registrationToDelete, setRegistrationToDelete] = useState<any | null>(null);
  const [selectedRegistrationDetails, setSelectedRegistrationDetails] = useState<any | null>(null);
  const [formData, setFormData] = useState<Omit<Event, "id">>({
    studio_id: "",
    event_name: "",
    assigned_instructors: [],
    start_time: "",
    end_time: "",
    type: "Regular",
    from_date: "",
    end_date: "",
    charge_for_event: false,
    charge_amount: 0,
    create_registration_link: false,
    registration_link: "",
    custom_terms_conditions: "",
    registration_fields: ["first_name", "last_name", "email", "phone"],
    auto_promote_after_testing: false,
    registration_title: "",
    registration_body: "",
    registration_image: "",
    show_studio_address_on_registration: false,
  });
  const [studioId, setStudioId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    title?: string;
  }>({
    show: false,
    message: "",
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");
  const [copyStatus, setCopyStatus] = useState<string>("");
  const [showRegistrationLinkModal, setShowRegistrationLinkModal] = useState(false);
  const [createdEvent, setCreatedEvent] = useState<Event | null>(null);
  const [chargeAmountInput, setChargeAmountInput] = useState<string>("");

  // Security-related state
  const [csrfToken, setCsrfToken] = useState<string>('');
  const [lastSubmissionTime, setLastSubmissionTime] = useState<number>(0);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/csrf-token`);
      if (response.ok) {
        const data = await response.json();
        if (data.token) {
          setCsrfToken(data.token);
        } else {
          logSecurityEvent('CSRF_TOKEN_INVALID_RESPONSE', { data });
        }
      } else {
        logSecurityEvent('CSRF_TOKEN_FETCH_ERROR', { status: response.status, statusText: response.statusText });
      }
    } catch (error) {
      logSecurityEvent('CSRF_TOKEN_FETCH_FAILED', { error: error instanceof Error ? error.message : 'Unknown error' });
      // In development, generate a fallback token
      if (process.env.NODE_ENV !== 'production') {
        const fallbackToken = Math.random().toString(36).substring(2) + Date.now().toString(36);
        setCsrfToken(fallbackToken);
        // eslint-disable-next-line no-console
        console.warn('Using fallback CSRF token in development mode');
      }
    }
  };

  // Export event data as JSON
  const exportEventData = async (event: Event) => {
    let registrationsData: any[] = [];
    
    // Fetch registrations if the event has a registration link
    if (event.create_registration_link) {
      try {
        const authToken = getAuthToken();
        if (authToken) {
          const [registrationsResponse, attendanceResponse] = await Promise.all([
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${event.id}/registrations`,
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
              }
            ),
            fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/${event.studio_id}?event_id=${event.id}&date=${event.from_date}`,
              {
                headers: {
                  Authorization: `Bearer ${authToken}`,
                },
              }
            )
          ]);

          if (registrationsResponse.ok) {
            const regData = await registrationsResponse.json();
            const attendanceData = attendanceResponse.ok ? await attendanceResponse.json() : [];
            
            // Add attendance status to registrations (simplified version)
            registrationsData = regData.map((registration: any) => ({
              ...registration,
              attendanceStatus: attendanceData.length > 0 ? 'unknown' : 'no-attendance-data'
            }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch registrations for export:', error);
      }
    }

    const exportData = {
      event: {
        id: event.id,
        studio_id: event.studio_id,
        event_name: event.event_name,
        assigned_instructors: event.assigned_instructors,
        start_time: event.start_time,
        end_time: event.end_time,
        type: event.type,
        from_date: event.from_date,
        end_date: event.end_date,
        charge_for_event: event.charge_for_event,
        charge_amount: event.charge_amount,
        create_registration_link: event.create_registration_link,
        registration_link: event.registration_link,
        registration_fields: event.registration_fields,
        auto_promote_after_testing: event.auto_promote_after_testing,
        registration_title: event.registration_title,
        registration_body: event.registration_body,
        registration_image: event.registration_image,
        custom_terms_conditions: event.custom_terms_conditions,
      },
      registrations: registrationsData,
      exported_at: new Date().toISOString(),
      export_metadata: {
        total_registrations: registrationsData.length,
        has_registrations: event.create_registration_link || false,
        registration_link: event.create_registration_link ? `${window.location.origin}/register/event/${event.id}` : null,
      }
    };

    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${event.event_name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_data_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setNotification({
      show: true,
      message: "Event data exported successfully",
      title: "Export Complete",
    });
  };

  useEffect(() => {
    const authToken = getAuthToken();
    if (!user || !authToken) return;
    
    // Fetch CSRF token first
    fetchCsrfToken();
    
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch studio data");
        return res.json();
      })
      .then((data) => {
        setStudioId(data.id);
        setStudioTimezone(data.timezone || "America/New_York");
      });
  }, [user, getAuthToken]);

  useEffect(() => {
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;
    loadData();
  }, [studioId, getAuthToken]);

  const loadData = async () => {
    const authToken = getAuthToken();
    if (!studioId || !authToken) return;
    setLoading(true);
    try {
      // Fetch studio data to check Stripe connection
      const studioResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!studioResponse.ok) {
        throw new Error("Failed to fetch studio data");
      }
      const studioData = await studioResponse.json();
      const hasStripe = !!studioData.stripe_user_id;
      setIsStripeConnected(hasStripe);

      // Fetch available instructors
      const instructorsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/staff/${studioId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!instructorsResponse.ok) {
        throw new Error("Failed to fetch instructors");
      }
      const instructorsData = await instructorsResponse.json();
      setAvailableInstructors(instructorsData);

      // Fetch events
      const eventsResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${studioId}`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!eventsResponse.ok) {
        throw new Error("Failed to fetch events");
      }
      const eventsData = await eventsResponse.json();

      // Hydrate assigned_instructors for each event
      const hydratedEvents = Array.isArray(eventsData)
        ? eventsData.map((eventObj) => ({
            ...eventObj,
            assigned_instructors: Array.isArray(eventObj.assigned_instructors)
              ? eventObj.assigned_instructors
                  .map((id: string) =>
                    instructorsData.find(
                      (inst: ClassInstructor) => inst.id === id
                    )
                  )
                  .filter(Boolean)
              : [],
          }))
        : [];

      setEvents(hydratedEvents);

    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load events",
        title: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditEvent = (event: Event) => {
    const { id, ...eventData } = event;
    
    // Convert database date format to MM/DD/YYYY for display
    const formattedEventData = {
      ...eventData,
      from_date: convertDatabaseDateToDisplay(eventData.from_date),
      end_date: convertDatabaseDateToDisplay(eventData.end_date),
    };
    
    setFormData(formattedEventData);
    setEditingEvent(event);
    setChargeAmountInput(
      typeof event.charge_amount === "number" && !isNaN(event.charge_amount)
        ? String(event.charge_amount)
        : ""
    );
    
    // Set image preview if event has an image
    if (event.registration_image) {
      setImagePreview(event.registration_image);
    } else {
      setImagePreview("");
    }
    
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingEvent(null);
    setImagePreview(""); // Clear image preview
    setImageFile(null); // Clear image file
    setChargeAmountInput("");
    setFormData({
      studio_id: "",
      event_name: "",
      assigned_instructors: [],
      start_time: "",
      end_time: "",
      type: "Regular",
      from_date: "",
      end_date: "",
      charge_for_event: false,
      charge_amount: 0,
      create_registration_link: false,
      registration_link: "",
      custom_terms_conditions: "",
      registration_fields: ["first_name", "last_name", "email", "phone"],
      auto_promote_after_testing: false,
      registration_title: "",
      registration_body: "",
      registration_image: "",
      show_studio_address_on_registration: false,
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    
    // Sanitize input based on field type
    let sanitizedValue = value;
    let fieldType = '';
    
    if (name === "from_date" || name === "end_date") {
      fieldType = 'dateInput';
      sanitizedValue = formatDateInput(value);
    } else if (name === "start_time" || name === "end_time") {
      fieldType = 'timeInput';
      sanitizedValue = sanitizeInput(value, fieldType);
    } else if (name === "event_name") {
      fieldType = 'eventName';
      sanitizedValue = sanitizeInput(value, fieldType);
    } else if (name === "registration_title") {
      fieldType = 'registrationTitle';
      sanitizedValue = sanitizeInput(value, fieldType);
    } else if (name === "registration_body") {
      fieldType = 'registrationBody';
      sanitizedValue = sanitizeInput(value, fieldType);
    } else if (name === "custom_terms_conditions") {
      fieldType = 'customTermsConditions';
      sanitizedValue = sanitizeInput(value, fieldType);
    } else if (name === "charge_amount") {
      fieldType = 'currencyInput';
      sanitizedValue = sanitizeInput(value, fieldType);
    } else {
      sanitizedValue = sanitizeInput(value);
    }
    
    // Validate input length
    if (fieldType && MAX_LENGTHS[fieldType] && sanitizedValue.length > MAX_LENGTHS[fieldType]) {
      sanitizedValue = sanitizedValue.substring(0, MAX_LENGTHS[fieldType]);
    }
    
    if (name === "from_date" || name === "end_date") {
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    } else if (name === "create_registration_link") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "true",
      }));
    } else if (name === "auto_promote_after_testing") {
      setFormData((prev) => ({
        ...prev,
        [name]: value === "true",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: sanitizedValue,
      }));
    }
  };

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // More aggressive compression - smaller max size for event images
        const maxSize = 400; // Reduced from 800px to 400px
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // More aggressive quality reduction (60% instead of 80%)
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
        resolve(compressedBase64);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.match(/image\/(jpeg|jpg|png|webp)/)) {
      setNotification({
        show: true,
        message: "Please select a JPEG, PNG, or WebP file only",
        title: "Invalid File Type",
      });
      return;
    }

    // Check file size (1MB = 1,048,576 bytes)
    if (file.size > 1048576) {
      setNotification({
        show: true,
        message: "File size must be less than 1MB",
        title: "File Too Large",
      });
      return;
    }

    setImageFile(file);
    
    try {
      // Compress and convert to base64
      const compressedBase64 = await compressImage(file);
      setImagePreview(compressedBase64);
      setFormData((prev) => ({
        ...prev,
        registration_image: compressedBase64,
      }));
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to process image. Please try again.",
        title: "Image Processing Error",
      });
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData((prev) => ({
      ...prev,
      registration_image: "",
    }));
  };

  const handleSaveEvent = async (e: React.FormEvent) => {
    e.preventDefault();
    const authToken = getAuthToken();
    if (!user || !authToken) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before saving the event again",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'saveEvent' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with submission');
      }
    }

    // Validate required fields
    if (!formData.event_name || !validateInput(formData.event_name, 'eventName')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'eventName', value: formData.event_name });
      setNotification({
        show: true,
        message: "Please enter a valid event name",
        title: "Validation Error",
      });
      return;
    }

    if (!formData.from_date || !validateInput(formData.from_date, 'dateInput')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'from_date', value: formData.from_date });
      setNotification({
        show: true,
        message: "Please enter a valid start date (MM/DD/YYYY)",
        title: "Validation Error",
      });
      return;
    }

    if (!formData.end_date || !validateInput(formData.end_date, 'dateInput')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'end_date', value: formData.end_date });
      setNotification({
        show: true,
        message: "Please enter a valid end date (MM/DD/YYYY)",
        title: "Validation Error",
      });
      return;
    }

    // Check if payment is enabled but Stripe is not connected
    if (formData.charge_for_event && !isStripeConnected) {
      setNotification({
        show: true,
        message: "Payment processing requires a connected Stripe account. Please connect your Stripe account in Settings → Integrations to enable payment features.",
        title: "Stripe Required",
      });
      return;
    }

    setLastSubmissionTime(now);

    try {
      // Fetch studio data
      const studioResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/studios/me`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      if (!studioResponse.ok) {
        throw new Error("Failed to fetch studio data");
      }
      const studioData = await studioResponse.json();

      const eventData = {
        ...formData,
        studio_id: studioData.id,
        from_date: convertDisplayDateToDatabase(formData.from_date) || new Date().toISOString(),
        end_date: convertDisplayDateToDatabase(formData.end_date) || new Date().toISOString(),
        assigned_instructors: formData.assigned_instructors.map((i) => i.id),
      };



      if (editingEvent) {
        // Update existing event
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${editingEvent.id}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(eventData),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to update event");
        }
        setNotification({
          show: true,
          message: "Event updated successfully",
          title: "Success",
        });
      } else {
        // Create new event
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authToken}`,
              "X-CSRF-Token": csrfToken,
            },
            body: JSON.stringify(eventData),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to create event");
        }
        
        const createdEventData = await response.json();
        
        // If the event has a registration link, show the registration link modal
        if (formData.create_registration_link && createdEventData.id) {
          setCreatedEvent(createdEventData);
          setShowRegistrationLinkModal(true);
        } else {
          setNotification({
            show: true,
            message: "Event added successfully",
            title: "Success",
          });
        }
      }

      handleModalClose();
      loadData();
    } catch (error) {
      logSecurityEvent('SAVE_EVENT_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        eventName: formData.event_name,
        isEditing: !!editingEvent
      });
      setNotification({
        show: true,
        message: "Failed to save event",
        title: "Error",
      });
    }
  };

  const handleDeleteEvent = (event: Event) => {
    setEventToDelete(event);
  };

  const confirmDelete = async () => {
    if (!eventToDelete) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before deleting another event",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation (strict in production, flexible in development)
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteEvent' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      } else {
        // In development, log the issue but allow submission to continue
        // eslint-disable-next-line no-console
        console.warn('CSRF token missing in development mode - proceeding with deletion');
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${eventToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();

        // Handle specific error for events with attendance records
        if (
          response.status === 400 &&
          errorData.error === "Cannot delete event with attendance records"
        ) {
          setNotification({
            show: true,
            message:
              "This event has attendance records and cannot be deleted unless you first delete the attendance records.",
            title: "Cannot Delete Event",
          });
        } else {
          setNotification({
            show: true,
            message: errorData.message || "Failed to delete event",
            title: "Error",
          });
        }
        setEventToDelete(null);
        return;
      }

      setEventToDelete(null);
      loadData();
      setNotification({
        show: true,
        message: "Event deleted successfully",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('DELETE_EVENT_ERROR', { 
        error: error instanceof Error ? error.message : 'Unknown error',
        eventId: eventToDelete?.id,
        eventName: eventToDelete?.event_name
      });
      setNotification({
        show: true,
        message: "Failed to delete event",
        title: "Error",
      });
      setEventToDelete(null);
    }
  };

  const cancelDelete = () => {
    setEventToDelete(null);
  };

  const handleViewRegistrations = async (event: Event) => {
    setViewingRegistrations(event);
    setLoadingRegistrations(true);
    setRegistrationSearchTerm("");
    
    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      // Fetch both registrations and attendance records
      const [registrationsResponse, attendanceResponse] = await Promise.all([
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/events/${event.id}/registrations`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        ),
        fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/supabase/attendance/${event.studio_id}?event_id=${event.id}&date=${event.from_date}`,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        )
      ]);

      if (!registrationsResponse.ok) {
        throw new Error("Failed to fetch registrations");
      }

      const registrationsData = await registrationsResponse.json();
      const attendanceData = attendanceResponse.ok ? await attendanceResponse.json() : [];

      // Fetch student and lead details for attendance records
      let studentsMap = new Map();
      let leadsMap = new Map();
      
      if (attendanceData.length > 0) {
        try {
          // Get unique student and lead IDs from attendance records
          const studentIds = Array.from(new Set(attendanceData.filter((r: any) => r.student_id).map((r: any) => r.student_id)));
          const leadIds = Array.from(new Set(attendanceData.filter((r: any) => r.lead_id).map((r: any) => r.lead_id)));
          
          // Fetch student details
          if (studentIds.length > 0) {
            const studentsResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/students/batch`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ ids: studentIds }),
              }
            );
            if (studentsResponse.ok) {
              const studentsData = await studentsResponse.json();
              studentsData.forEach((student: any) => {
                studentsMap.set(student.id, student);
              });
            }
          }
          
          // Fetch lead details
          if (leadIds.length > 0) {
            const leadsResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/supabase/leads/batch`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({ ids: leadIds }),
              }
            );
            if (leadsResponse.ok) {
              const leadsData = await leadsResponse.json();
              leadsData.forEach((lead: any) => {
                leadsMap.set(lead.id, lead);
              });
            }
          }
        } catch (error) {
          console.error('Failed to fetch student/lead details:', error);
        }
      }

      // Add attendance status to each registration
      const registrationsWithAttendance = registrationsData.map((registration: any) => {
        const registrationEmail = registration.registration_data?.email?.toLowerCase();
        const registrationName = `${registration.registration_data?.firstName || ''} ${registration.registration_data?.lastName || ''}`.toLowerCase().trim();
        
        // Check if this registration has a corresponding attendance record
        let hasAttendance = false;
        let attendanceRecord = null;
        
        // Try to match by email or phone number (most reliable)
        if (registrationEmail || registration.registration_data?.phone) {
          const registrationPhone = registration.registration_data?.phone?.replace(/\D/g, ''); // Remove non-digits
          
          if (attendanceData.length > 0) {
            
            for (const attendanceRecord of attendanceData) {
              let personEmail = null;
              let personPhone = null;
              
              // Get email and phone from student or lead
              if (attendanceRecord.student_id && studentsMap.has(attendanceRecord.student_id)) {
                const student = studentsMap.get(attendanceRecord.student_id);
                personEmail = student?.email?.toLowerCase();
                personPhone = student?.phone?.replace(/\D/g, ''); // Remove non-digits

              } else if (attendanceRecord.lead_id && leadsMap.has(attendanceRecord.lead_id)) {
                const lead = leadsMap.get(attendanceRecord.lead_id);
                personEmail = lead?.email?.toLowerCase();
                personPhone = lead?.phone?.replace(/\D/g, ''); // Remove non-digits

              }
              
              // Match by email OR phone number
              const emailMatch = personEmail && registrationEmail && personEmail.toLowerCase() === registrationEmail.toLowerCase();
              const phoneMatch = personPhone && registrationPhone && personPhone === registrationPhone;
              
              // Enhanced name matching
              const personName = attendanceRecord.student_id 
                ? studentsMap.get(attendanceRecord.student_id)?.name?.toLowerCase()
                : leadsMap.get(attendanceRecord.lead_id)?.name?.toLowerCase();
              
              let nameMatch = false;
              if (personName && registrationName) {
                // Split names into parts for better matching
                const personNameParts = personName.split(' ').filter((part: string) => part.length > 0);
                const registrationNameParts = registrationName.split(' ').filter((part: string) => part.length > 0);
                
                // Check if any name parts match (allowing for partial matches)
                const matchingParts = personNameParts.filter((part: string) => 
                  registrationNameParts.some((regPart: string) => 
                    part.includes(regPart) || regPart.includes(part)
                  )
                );
                
                // Consider it a match if at least 2 name parts match, or if one part matches and it's longer than 2 characters
                nameMatch = matchingParts.length >= 2 || 
                           (matchingParts.length === 1 && matchingParts[0].length > 2);
              }
              
              if (emailMatch || phoneMatch || nameMatch) {
                hasAttendance = true;
                break;
              }
            }
          }
        }
        
        return {
          ...registration,
          attendanceStatus: hasAttendance ? 'attended' : 'not-attended',
          attendanceRecord
        };
      });

      setRegistrations(registrationsWithAttendance);
      setFilteredRegistrations(registrationsWithAttendance);
    } catch (error) {
      setNotification({
        show: true,
        message: "Failed to load registrations",
        title: "Error",
      });
    } finally {
      setLoadingRegistrations(false);
    }
  };

  const handleRegistrationUpdated = (updated: any) => {
    setSelectedRegistrationDetails(updated);
    setRegistrations((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setFilteredRegistrations((prev) => prev.map((r) => (r.id === updated.id ? updated : r)));
    setNotification({
      show: true,
      message: "Registration updated successfully",
      title: "Success",
    });
  };

  const handleRegistrationSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value, 'searchTerm');
    
    // Allow empty search terms (for clearing the search)
    if (sanitizedValue === '') {
      setRegistrationSearchTerm(sanitizedValue);
      applyFilters(sanitizedValue);
      return;
    }
    
    // Enhanced validation only for non-empty values
    if (!validateInput(sanitizedValue, 'searchTerm')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'registrationSearchTerm', value: sanitizedValue.substring(0, 50) + '...' });
      setNotification({
        show: true,
        message: "Invalid search term. Please check your input and try again.",
        title: "Validation Error",
      });
      return;
    }

    // Check length limits
    if (sanitizedValue.length > MAX_LENGTHS.searchTerm) {
      setNotification({
        show: true,
        message: `Search term too long. Maximum ${MAX_LENGTHS.searchTerm} characters.`,
        title: "Validation Error",
      });
      return;
    }

    setRegistrationSearchTerm(sanitizedValue);
    applyFilters(sanitizedValue);
  };


  const applyFilters = (searchTerm: string) => {
    let filtered = registrations;
    
    // Apply search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter((registration) => {
        const firstName = registration.registration_data?.firstName?.toLowerCase() || "";
        const lastName = registration.registration_data?.lastName?.toLowerCase() || "";
        const email = registration.registration_data?.email?.toLowerCase() || "";
        const phone = registration.registration_data?.phone?.toLowerCase() || "";
        
        return firstName.includes(searchTerm) || 
               lastName.includes(searchTerm) || 
               email.includes(searchTerm) || 
               phone.includes(searchTerm);
      });
    }
    
    setFilteredRegistrations(filtered);
  };

  const closeRegistrationsModal = () => {
    setViewingRegistrations(null);
    setSelectedRegistrationDetails(null);
    setRegistrations([]);
    setFilteredRegistrations([]);
    setRegistrationSearchTerm("");
    setRegistrationToDelete(null);
  };

  const handleExportRegistrations = () => {
    if (!viewingRegistrations) return;

    if (registrations.length === 0) {
      setNotification({
        show: true,
        message: "No registrations to export",
        title: "Export",
      });
      return;
    }

    exportRegistrationsToExcel(
      registrations,
      viewingRegistrations.event_name,
      viewingRegistrations.registration_fields,
      { includeAttendance: true }
    );

    setNotification({
      show: true,
      message: "Registrations exported successfully",
      title: "Export Complete",
    });
  };

  const handleDeleteRegistration = (registration: any) => {
    setRegistrationToDelete(registration);
  };

  const confirmDeleteRegistration = async () => {
    if (!registrationToDelete || !viewingRegistrations) return;

    // Client-side rate limiting
    const now = Date.now();
    if (now - lastSubmissionTime < SUBMISSION_COOLDOWN) {
      setNotification({
        show: true,
        message: "Please wait before deleting another registration",
        title: "Rate Limited",
      });
      return;
    }

    // CSRF token validation
    if (!csrfToken) {
      logSecurityEvent('CSRF_TOKEN_MISSING', { action: 'deleteRegistration' });
      if (process.env.NODE_ENV === 'production') {
        setNotification({
          show: true,
          message: "Security token missing. Please refresh the page and try again.",
          title: "Error",
        });
        return;
      }
    }

    setLastSubmissionTime(now);

    try {
      const authToken = getAuthToken();
      if (!authToken) return;

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/event-registrations/${registrationToDelete.id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "X-CSRF-Token": csrfToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to delete registration");
      }

      // Remove the deleted registration from the local state
      const updatedRegistrations = registrations.filter(
        (reg) => reg.id !== registrationToDelete.id
      );
      setRegistrations(updatedRegistrations);
      
      // Update filtered registrations as well
      const updatedFilteredRegistrations = filteredRegistrations.filter(
        (reg) => reg.id !== registrationToDelete.id
      );
      setFilteredRegistrations(updatedFilteredRegistrations);

      setRegistrationToDelete(null);
      setNotification({
        show: true,
        message: "Registration deleted successfully",
        title: "Success",
      });
    } catch (error) {
      logSecurityEvent('DELETE_REGISTRATION_ERROR', {
        error: error instanceof Error ? error.message : 'Unknown error',
        registrationId: registrationToDelete?.id,
        eventId: viewingRegistrations?.id,
      });
      setNotification({
        show: true,
        message: error instanceof Error ? error.message : "Failed to delete registration",
        title: "Error",
      });
      setRegistrationToDelete(null);
    }
  };

  const cancelDeleteRegistration = () => {
    setRegistrationToDelete(null);
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(":");
    const date = new Date();
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));

    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatDate = (dateStr: string) => {
    // Handle both ISO date strings and YYYY-MM-DD format consistently
    let date: Date;
    if (dateStr.includes('T')) {
      // ISO date string like "2025-07-18T00:00:00+00:00"
      date = new Date(dateStr);
    } else {
      // YYYY-MM-DD format - create date at midnight in local timezone
      const [year, month, day] = dateStr.split('-').map(Number);
      date = new Date(year, month - 1, day, 0, 0, 0, 0);
    }
    
    return date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Handler for search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitizedValue = sanitizeInput(e.target.value, 'searchTerm');
    
    // Allow empty search terms (for clearing the search)
    if (sanitizedValue === '') {
      setSearchTerm(sanitizedValue);
      return;
    }
    
    // Enhanced validation only for non-empty values
    if (!validateInput(sanitizedValue, 'searchTerm')) {
      logSecurityEvent('INVALID_INPUT_ATTEMPT', { field: 'searchTerm', value: sanitizedValue.substring(0, 50) + '...' });
      setNotification({
        show: true,
        message: "Invalid search term. Please check your input and try again.",
        title: "Validation Error",
      });
      return;
    }

    // Check length limits
    if (sanitizedValue.length > MAX_LENGTHS.searchTerm) {
      setNotification({
        show: true,
        message: `Search term too long. Maximum ${MAX_LENGTHS.searchTerm} characters.`,
        title: "Validation Error",
      });
      return;
    }

    setSearchTerm(sanitizedValue);
  };

  const filteredEvents = events.filter((event) =>
    event.event_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Utility function for consistent input styling
  const getInputClassName = (baseClasses: string) => {
    return `${baseClasses} bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600`;
  };

  // Copy registration link to clipboard
  const copyRegistrationLink = async () => {
    const link = editingEvent 
      ? `${window.location.origin}/register/event/${editingEvent.id}`
      : `${window.location.origin}/register/event/[event-id]`;
    
    try {
      await navigator.clipboard.writeText(link);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus(""), 2000);
    } catch (err) {
      setCopyStatus("Failed to copy");
      setTimeout(() => setCopyStatus(""), 2000);
    }
  };

  // Copy registration link for created event
  const copyCreatedEventLink = async () => {
    if (!createdEvent) return;
    
    const link = `${window.location.origin}/register/event/${createdEvent.id}`;
    
    try {
      await navigator.clipboard.writeText(link);
      setCopyStatus("Copied!");
      setTimeout(() => setCopyStatus(""), 2000);
    } catch (err) {
      setCopyStatus("Failed to copy");
      setTimeout(() => setCopyStatus(""), 2000);
    }
  };

  // Close registration link modal
  const closeRegistrationLinkModal = () => {
    setShowRegistrationLinkModal(false);
    setCreatedEvent(null);
    setCopyStatus("");
  };


  return (
    <div className="relative bg-gray-50 dark:bg-gray-900">
      <div className="space-y-4 p-0 pt-6 xl:pt-0 xl:p-6">
        <Notification
          show={notification.show}
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification((prev) => ({ ...prev, show: false }))}
        />
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Event Management
            </h1>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manage your studio events and schedules
            </p>
          </div>
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 w-fit xl:w-auto"
          >
            <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5 pointer-events-none" aria-hidden="true" />
            <span className="pointer-events-none">Add Event</span>
          </button>
        </div>

        {isModalOpen && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-lg xl:max-w-2xl sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={handleModalClose}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left w-full">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      {editingEvent ? "Edit Event" : "Add New Event"}
                    </h3>
                    <div className="mt-2">
                      <form className="space-y-4" onSubmit={handleSaveEvent}>
                        <div>
                          <label
                            htmlFor="event_name"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Event Name
                          </label>
                          <input
                            type="text"
                            name="event_name"
                            id="event_name"
                            required
                            maxLength={MAX_LENGTHS.eventName}
                            value={formData.event_name}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="from_date"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            From Date (MM/DD/YYYY)
                          </label>
                          <input
                            type="text"
                            name="from_date"
                            id="from_date"
                            required
                            maxLength={MAX_LENGTHS.dateInput}
                            value={formData.from_date}
                            onChange={handleInputChange}
                            placeholder="MM/DD/YYYY"
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="end_date"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            End Date (MM/DD/YYYY)
                          </label>
                          <input
                            type="text"
                            name="end_date"
                            id="end_date"
                            required
                            maxLength={MAX_LENGTHS.dateInput}
                            value={formData.end_date}
                            onChange={handleInputChange}
                            placeholder="MM/DD/YYYY"
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="start_time"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Start Time
                          </label>
                          <input
                            type="time"
                            name="start_time"
                            id="start_time"
                            required
                            maxLength={MAX_LENGTHS.timeInput}
                            value={formData.start_time}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="end_time"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            End Time
                          </label>
                          <input
                            type="time"
                            name="end_time"
                            id="end_time"
                            required
                            maxLength={MAX_LENGTHS.timeInput}
                            value={formData.end_time}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          />
                        </div>
                        <div>
                          <label
                            htmlFor="type"
                            className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                          >
                            Type
                          </label>
                          <select
                            id="type"
                            name="type"
                            required
                            value={formData.type}
                            onChange={handleInputChange}
                            className={getInputClassName(
                              "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            )}
                          >
                            <option value="Regular">Regular</option>
                            <option value="Special">Special</option>
                            <option value="Private">Private</option>
                            <option value="Testing">Belt Testing/Promotions</option>
                            <option value="Workshop/Seminar">
                              Workshop/Seminar
                            </option>
                            <option value="Party">Party</option>
                            <option value="Tournament">Tournament</option>
                            <option value="Parent's Night Out">
                              Parent's Night Out
                            </option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Instructors
                          </label>
                          <div className="space-y-2">
                            {availableInstructors.map((instructor) => (
                              <label
                                key={instructor.id}
                                className="flex items-center space-x-2"
                              >
                                <input
                                  type="checkbox"
                                  checked={formData.assigned_instructors.some(
                                    (i) => i.id === instructor.id
                                  )}
                                  onChange={(e) => {
                                    const newInstructors = e.target.checked
                                      ? [
                                          ...formData.assigned_instructors,
                                          instructor,
                                        ]
                                      : formData.assigned_instructors.filter(
                                          (i) => i.id !== instructor.id
                                        );
                                    setFormData((prev) => ({
                                      ...prev,
                                      assigned_instructors: newInstructors,
                                    }));
                                  }}
                                  className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  {instructor.name}
                                  {instructor.belt
                                    ? ` (${instructor.belt})`
                                    : ""}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Let people register on a public landing page for this event?
                          </label>
                          <div className="space-y-2">
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="create_registration_link"
                                value="false"
                                checked={!formData.create_registration_link}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                No
                              </span>
                            </label>
                            <label className="flex items-center space-x-2">
                              <input
                                type="radio"
                                name="create_registration_link"
                                value="true"
                                checked={formData.create_registration_link}
                                onChange={handleInputChange}
                                className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                              />
                              <span className="text-sm text-gray-700 dark:text-gray-300">
                                Yes
                              </span>
                            </label>
                          </div>
                        </div>
                        {formData.create_registration_link && (
                          <>
                            <div>
                              <label
                                htmlFor="registration_title"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                Registration Page Title
                              </label>
                              <input
                                type="text"
                                name="registration_title"
                                id="registration_title"
                                maxLength={MAX_LENGTHS.registrationTitle}
                                value={formData.registration_title}
                                onChange={handleInputChange}
                                placeholder="Enter a title for the registration page"
                                className={getInputClassName(
                                  "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                )}
                              />
                            </div>
                            <div>
                              <label
                                htmlFor="registration_body"
                                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                              >
                                Registration Page Description
                              </label>
                              <textarea
                                name="registration_body"
                                id="registration_body"
                                rows={4}
                                maxLength={MAX_LENGTHS.registrationBody}
                                value={formData.registration_body}
                                onChange={handleInputChange}
                                placeholder="Enter description and details about the event..."
                                className={getInputClassName(
                                  "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                )}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Event Image
                              </label>
                                                          <div className="mt-1">
                              {imagePreview ? (
                                <div className="space-y-2">
                                  <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="mx-auto h-32 w-auto object-contain border border-gray-300 dark:border-gray-600 rounded-md"
                                  />
                                  <div className="flex justify-center space-x-2">
                                    <button
                                      type="button"
                                      onClick={removeImage}
                                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                      <span className="pointer-events-none">Remove Image</span>
                                    </button>
                                    <label
                                      htmlFor="image-upload"
                                      className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm leading-4 font-medium rounded-md text-indigo-600 dark:text-indigo-400 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                                    >
                                      <span className="pointer-events-none">Change Image</span>
                                      <input
                                        id="image-upload"
                                        name="image-upload"
                                        type="file"
                                        className="sr-only"
                                        accept="image/jpeg,image/jpg,image/png,image/webp"
                                        onChange={handleImageUpload}
                                      />
                                    </label>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <label
                                    htmlFor="image-upload"
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
                                  >
                                    <span className="pointer-events-none">Upload a file</span>
                                    <input
                                      id="image-upload"
                                      name="image-upload"
                                      type="file"
                                      className="sr-only"
                                      accept="image/jpeg,image/jpg,image/png,image/webp"
                                      onChange={handleImageUpload}
                                    />
                                  </label>
                                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                    PNG, JPG, JPEG, WebP up to 1MB (will be compressed)
                                  </p>
                                </div>
                              )}
                            </div>
                            </div>
                          </>
                        )}
                        {formData.create_registration_link && (
                          <>
                            <div>
                              <label className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  checked={formData.show_studio_address_on_registration || false}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      show_studio_address_on_registration: e.target.checked,
                                    }));
                                  }}
                                  className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Show studio address on registration page (replaces event type)
                                </span>
                              </label>
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 ml-6">
                                Uses the address from Settings → Profile. If no address is set, the event type will still be shown.
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Registration Form Fields
                              </label>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                              Select which fields to include on the registration form
                            </p>
                            <div className="mb-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
                              <p className="text-xs text-blue-800 dark:text-blue-200">
                                <strong>Attendance Tracking:</strong> First Name, Last Name, Email, and Phone Number are used to match registrations with actual attendance records. For accurate attendance tracking, registrants must be existing students or leads in your system.
                              </p>
                            </div>
                            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-md p-3">
                              {defaultRegistrationFields.map((field) => (
                                <label
                                  key={field.id}
                                  className="flex items-center space-x-2"
                                >
                                  <input
                                    type="checkbox"
                                    checked={formData.registration_fields?.includes(field.id) || field.required}
                                    disabled={field.required}
                                    onChange={(e) => {
                                      const currentFields = formData.registration_fields || [];
                                      const newFields = e.target.checked
                                        ? [...currentFields, field.id]
                                        : currentFields.filter((f) => f !== field.id);
                                      setFormData((prev) => ({
                                        ...prev,
                                        registration_fields: newFields,
                                      }));
                                    }}
                                    className="h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700"
                                  />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    {field.label}
                                    {field.required && (
                                      <span className="text-red-500 ml-1">*</span>
                                    )}
                                  </span>
                                </label>
                              ))}
                              <label className={`flex items-center space-x-2 ${!isStripeConnected ? 'opacity-50' : ''}`}>
                                <input
                                  type="checkbox"
                                  checked={formData.charge_for_event}
                                  disabled={!isStripeConnected}
                                  onChange={(e) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      charge_for_event: e.target.checked,
                                      charge_amount: e.target.checked ? prev.charge_amount : 0,
                                    }));
                                    setChargeAmountInput((prevInput) => (e.target.checked ? prevInput : ""));
                                  }}
                                  className={`h-4 w-4 text-indigo-600 dark:text-indigo-400 border-gray-300 dark:border-gray-600 rounded focus:ring-indigo-500 dark:focus:ring-indigo-400 bg-white dark:bg-gray-700 ${!isStripeConnected ? 'cursor-not-allowed' : ''}`}
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Payment Form
                                  {!isStripeConnected && (
                                    <span className="text-xs text-red-500 ml-1">(Requires Stripe)</span>
                                  )}
                                </span>
                                {!isStripeConnected && (
                                  <div className="ml-2 relative group">
                                    <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal w-64 z-10">
                                      Connect your Stripe account in Settings → Integrations to enable payment processing for event registrations.
                                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                                    </div>
                                  </div>
                                )}
                                {formData.charge_for_event && (
                                  <div className="flex items-center space-x-2 ml-4">
                                    <input
                                      type="text"
                                      name="charge_amount"
                                      maxLength={MAX_LENGTHS.currencyInput}
                                      value={chargeAmountInput}
                                      onChange={(e) => {
                                        const rawValue = e.target.value;
                                        setChargeAmountInput(rawValue);
                                        const numericValue = parseCurrencyInput(rawValue);
                                        setFormData((prev) => ({
                                          ...prev,
                                          charge_amount: numericValue,
                                        }));
                                      }}
                                      className="w-24 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                                      placeholder="$0.00"
                                    />
                                  </div>
                                )}
                              </label>
                            </div>
                            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                              * Required fields cannot be removed
                            </p>
                            
                            {/* Custom Terms & Conditions */}
                            {formData.registration_fields?.includes("terms_conditions") && (
                              <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Custom Terms & Conditions
                                </label>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                  Customize the terms and conditions text that registrants will see. Leave blank to use the default text.
                                </p>
                                <textarea
                                  name="custom_terms_conditions"
                                  value={formData.custom_terms_conditions || ""}
                                  onChange={handleInputChange}
                                  rows={4}
                                  maxLength={MAX_LENGTHS.customTermsConditions}
                                  className={`w-full px-3 py-2 border rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 ${getInputClassName("")}`}
                                  placeholder="I agree to the terms and conditions for this event. By checking this box, I acknowledge that I have read and agree to comply with all event policies, safety guidelines, and studio rules."
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                  If left blank, the default terms and conditions will be used.
                                </p>
                                {formData.custom_terms_conditions && (
                                  <div className="mt-2 p-2 bg-gray-50 dark:bg-gray-700 rounded border">
                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Preview:</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400">
                                      {formData.custom_terms_conditions}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          </>
                        )}
                        {formData.create_registration_link && (
                          <div>
                            <div className="mt-3">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                  Registration Link:
                                </span>
                                <div className="relative group">
                                  <InformationCircleIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 cursor-help" />
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-3 bg-gray-900 dark:bg-gray-700 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none w-80 max-w-xs z-10 whitespace-normal">
                                    <div className="break-words">
                                      Share this link to send people to your public event page (landing content) where they can register.
                                    </div>
                                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900 dark:border-t-gray-700"></div>
                                  </div>
                                </div>
                              </div>
                              <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md p-3">
                                <div className="flex items-center justify-between">
                                  <code className="text-sm text-gray-800 dark:text-gray-200 break-all flex-1 mr-2">
                                    {editingEvent 
                                      ? `${window.location.origin}/register/event/${editingEvent.id}`
                                      : `${window.location.origin}/register/event/[event-id]`
                                    }
                                  </code>
                                  <button
                                    type="button"
                                    onClick={copyRegistrationLink}
                                    className="flex-shrink-0 inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                                  >
                                    <ClipboardDocumentIcon className="h-4 w-4 mr-1 pointer-events-none" />
                                    <span className="pointer-events-none">{copyStatus || "Copy"}</span>
                                  </button>
                                </div>
                              </div>
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {editingEvent 
                                  ? "This link will be available after you save the event."
                                  : "The link will be generated when you create the event."
                                }
                              </p>
                            </div>
                          </div>
                        )}
                        <div className="mt-6 xl:mt-4 flex flex-col xl:flex-row xl:flex-row-reverse gap-3 xl:gap-0">
                          <button
                            type="submit"
                            className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 xl:ml-3 xl:w-auto"
                          >
                            <span className="pointer-events-none">{editingEvent ? "Update" : "Add Event"}</span>
                          </button>
                          {editingEvent && (
                            <button
                              type="button"
                              onClick={() => exportEventData(editingEvent)}
                              className="inline-flex w-full justify-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 xl:ml-3 xl:w-auto"
                            >
                              <svg className="h-4 w-4 mr-2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="pointer-events-none">Export Data</span>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleModalClose}
                            className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 xl:w-auto"
                          >
                            <span className="pointer-events-none">Cancel</span>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="relative">
          <input
            type="text"
            maxLength={MAX_LENGTHS.searchTerm}
            className={getInputClassName(
              "block w-full rounded-md border-0 py-1.5 pl-3 pr-10 ring-1 ring-inset placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            )}
            placeholder="Search events..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* Loading Message */}
        {loading && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-pulse flex space-x-4 items-center">
              <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin"></div>
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Loading events...
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {!loading && (
          <div className="mt-8 relative">
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full py-2 align-middle">
                <div className="relative overflow-hidden bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                    <thead>
                      <tr>
                        <th
                          scope="col"
                          className="py-3.5 pl-0 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Event Name
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Instructors
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Date
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Time
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="py-3.5 px-3 text-left text-sm font-bold font-sans text-gray-900 dark:text-white"
                        >
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {filteredEvents.map((event) => (
                        <tr key={event.id}>
                          <td className="whitespace-nowrap py-4 pl-0 text-sm font-medium text-gray-900 dark:text-white">
                            {event.event_name}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {Array.isArray(event.assigned_instructors)
                              ? event.assigned_instructors
                                  .map((i) => i?.name)
                                  .join(", ")
                              : ""}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              {formatDate(event.from_date)}
                              {event.from_date !== event.end_date && (
                                <>
                                  <span className="text-gray-300 dark:text-gray-600">-</span>
                                  {formatDate(event.end_date)}
                                </>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            <div className="flex items-center gap-2">
                              <ClockIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                              {formatTime(event.start_time)} -{" "}
                              {formatTime(event.end_time)}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                            {event.type}
                          </td>
                          <td className="relative whitespace-nowrap py-4 px-3 text-right text-sm font-medium">
                            <div className="flex justify-end space-x-2">
                              {event.create_registration_link && (
                                <a
                                  href={`/register/event/${event.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="View public landing page and registration form"
                                >
                                  <svg
                                    className="h-5 w-5 text-blue-500 dark:text-blue-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                    />
                                  </svg>
                                </a>
                              )}
                              <button
                                type="button"
                                onClick={() => handleEditEvent(event)}
                                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
                              >
                                <PencilIcon className="h-5 w-5 pointer-events-none" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDeleteEvent(event)}
                                className="bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-gray-100 dark:hover:bg-gray-600 rounded p-2"
                              >
                                <TrashIcon className="h-5 w-5 pointer-events-none" />
                              </button>
                              {event.create_registration_link && (
                                <button
                                  type="button"
                                  onClick={() => handleViewRegistrations(event)}
                                  className="p-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                                  title="View registrations"
                                >
                                  <UserIcon className="h-5 w-5 inline-block mr-1 text-indigo-600 dark:text-indigo-400 pointer-events-none" />
                                  <span className="text-indigo-600 dark:text-indigo-400 font-medium pointer-events-none">
                                    View Registrations
                                  </span>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {eventToDelete && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div>
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Delete Event
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete "
                        {eventToDelete.event_name}"? This action cannot be
                        undone.
                      </p>
                    </div>
                  </div>
                <div className="mt-5 sm:mt-4 flex flex-col xl:flex-row xl:justify-end space-y-3 xl:space-y-0 xl:space-x-3">
                  <button
                    type="button"
                    onClick={cancelDelete}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                  <button
                    type="button"
                    onClick={confirmDelete}
                    className="w-full xl:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Registrations Modal */}
        {viewingRegistrations && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6">
                <div className="absolute right-0 top-0 z-10 flex items-center gap-2 p-4">
                  <button
                    type="button"
                    onClick={handleExportRegistrations}
                    disabled={loadingRegistrations || registrations.length === 0}
                    className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1.5 pointer-events-none" />
                    <span className="pointer-events-none">Export Excel</span>
                  </button>
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={closeRegistrationsModal}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="w-full">
                  <div className="mt-3 pr-44">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Registrations for "{viewingRegistrations.event_name}"
                    </h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <UserIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {registrations.length} registrations
                      </span>
                    </div>
                  </div>
                    
                    {/* Search Bar */}
                    {!selectedRegistrationDetails && (
                    <div className="mt-4">
                      <div className="flex space-x-4">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            maxLength={MAX_LENGTHS.searchTerm}
                            placeholder="Search registrations by name, email, or phone..."
                            value={registrationSearchTerm}
                            onChange={handleRegistrationSearch}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      {registrationSearchTerm && (
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                          Showing {filteredRegistrations.length} of {registrations.length} registrations
                        </p>
                      )}
                    </div>
                    )}

                    
                    <div className="mt-4">
                      {selectedRegistrationDetails ? (
                        <RegistrationDetailsPanel
                          registration={selectedRegistrationDetails}
                          registrationFields={viewingRegistrations.registration_fields}
                          customTermsConditions={viewingRegistrations.custom_terms_conditions}
                          onBack={() => setSelectedRegistrationDetails(null)}
                          onUpdated={handleRegistrationUpdated}
                          getAuthToken={getAuthToken}
                        />
                      ) : loadingRegistrations ? (
                        <div className="flex justify-center items-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading registrations...</span>
                        </div>
                      ) : registrations.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No registrations found for this event.</p>
                        </div>
                      ) : filteredRegistrations.length === 0 ? (
                        <div className="text-center py-8">
                          <p className="text-gray-500 dark:text-gray-400">No registrations match your search criteria.</p>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          {(() => {
                            const extraColumns = getExtraRegistrationColumns(viewingRegistrations.registration_fields);
                            return (
                          <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                            <thead>
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Phone
                                </th>
                                {extraColumns.map((fieldId) => (
                                  <th
                                    key={fieldId}
                                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                                  >
                                    {getRegistrationFieldLabel(fieldId)}
                                  </th>
                                ))}
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Submitted
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                  Actions
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                              {filteredRegistrations.map((registration, index) => (
                                <tr key={registration.id || index}>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                    {registration.registration_data?.firstName} {registration.registration_data?.lastName}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {registration.registration_data?.email}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {registration.registration_data?.phone || '-'}
                                  </td>
                                  {extraColumns.map((fieldId) => (
                                    <td
                                      key={fieldId}
                                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                                    >
                                      {getRegistrationFieldValue(registration.registration_data, fieldId)}
                                    </td>
                                  ))}
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {registration.submitted_at ? new Date(registration.submitted_at).toLocaleDateString() : '-'}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    <div className="flex items-center space-x-1">
                                      <button
                                        type="button"
                                        onClick={() => setSelectedRegistrationDetails(registration)}
                                        className="inline-flex items-center justify-center p-1.5 text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-md transition-colors"
                                        title="View registration details"
                                      >
                                        <EyeIcon className="h-5 w-5 pointer-events-none" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteRegistration(registration)}
                                        className="inline-flex items-center justify-center p-1.5 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                                        title="Delete registration"
                                      >
                                        <XMarkIcon className="h-5 w-5 pointer-events-none" />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeRegistrationsModal}
                    className="inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Close</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Registration Confirmation Modal */}
        {registrationToDelete && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon
                      className="h-6 w-6 text-red-600 dark:text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-left sm:ml-4">
                    <h3 className="text-base font-semibold leading-6 text-gray-900 dark:text-white">
                      Delete Registration
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Are you sure you want to delete the registration for{" "}
                        <strong>
                          {registrationToDelete.registration_data?.firstName}{" "}
                          {registrationToDelete.registration_data?.lastName}
                        </strong>
                        ? This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={confirmDeleteRegistration}
                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Delete</span>
                  </button>
                  <button
                    type="button"
                    onClick={cancelDeleteRegistration}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 sm:mt-0 sm:w-auto"
                  >
                    <span className="pointer-events-none">Cancel</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Registration Link Modal */}
        {showRegistrationLinkModal && createdEvent && (
          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
              <div className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                    onClick={closeRegistrationLinkModal}
                  >
                    <span className="sr-only pointer-events-none">Close</span>
                    <XMarkIcon className="h-6 w-6 pointer-events-none" aria-hidden="true" />
                  </button>
                </div>
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-left w-full sm:ml-4">
                    <div className="flex items-center space-x-2 mb-4">
                      <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                      <h3 className="text-lg font-semibold leading-6 text-gray-900 dark:text-white">
                        Event Created Successfully!
                      </h3>
                    </div>
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                        Your event "{createdEvent.event_name}" has been created. Here's the link to your public event page (landing + registration) to share with attendees:
                      </p>
                      <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md p-3">
                        <div className="flex items-center justify-between">
                          <code className="text-sm text-gray-800 dark:text-gray-200 break-all flex-1 mr-2">
                            {`${window.location.origin}/register/event/${createdEvent.id}`}
                          </code>
                          <button
                            type="button"
                            onClick={copyCreatedEventLink}
                            className="flex-shrink-0 inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors"
                          >
                            <ClipboardDocumentIcon className="h-4 w-4 mr-1 pointer-events-none" />
                            <span className="pointer-events-none">{copyStatus || "Copy"}</span>
                          </button>
                        </div>
                      </div>
                      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                        Share this link so people can view event details and register. You can find it later by editing the event.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={closeRegistrationLinkModal}
                    className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:ml-3 sm:w-auto"
                  >
                    <span className="pointer-events-none">Done</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const defaultRegistrationFields = [
  { id: "first_name", label: "First Name", required: true },
  { id: "last_name", label: "Last Name", required: true },
  { id: "email", label: "Email Address", required: true },
  { id: "phone", label: "Phone Number", required: true },
  { id: "date_of_birth", label: "Date of Birth", required: false },
  { id: "address", label: "Address", required: false },
  { id: "emergency_contact", label: "Emergency Contact", required: false },
  { id: "allergies", label: "Allergies/Medical Information", required: false },
  { id: "medical_info", label: "Medical Information", required: false },
  { id: "experience_level", label: "Martial Arts Experience", required: false },
  { id: "belt_rank", label: "Current Belt Rank", required: false },
  { id: "belt_size", label: "Belt Size", required: false },
  { id: "uniform_size", label: "Uniform Size", required: false },
  { id: "t_shirt_size", label: "T-Shirt Size", required: false },
  { id: "how_did_you_hear", label: "How Did You Hear About Us?", required: false },
  { id: "parent_name", label: "Parent/Guardian Name", required: false },
  { id: "parent_phone", label: "Parent/Guardian Phone", required: false },
  { id: "parent_email", label: "Parent/Guardian Email", required: false },
  { id: "terms_conditions", label: "Terms & Conditions", required: false },
];